import React, { useCallback, useRef, useState, Children, isValidElement, useMemo } from 'react';
import { C as ComboboxContent } from './ComboboxContent-aF5wLcxQ.js';
import { C as ComboboxAction } from './ComboboxAction-Cl009-p8.js';
import { C as ComboboxContextProvider } from './ComboboxProvider-DdsdhWRT.js';
import { C as ComboboxTrigger } from './ComboboxTrigger-CNG9SkM_.js';
import { C as ComboboxOption } from './ComboboxOption-4NlS0P16.js';
import { d as debounce } from './debounce-ClFpMKL7.js';
import { n as noop } from './noop-BzN7sYrl.js';
import { C as ComboboxActivator } from './ComboboxActivator-r-CFzU94.js';
import { u as useAssert_2 } from './useAssert-TYWDgAXU.js';

var styles = {"overlay":"W8qMZnORYxI-","wrapper":"_-0dkolvLF-Y-"};

function useMakeComboboxHandlers(setOpen, setSearchValue, selectedOptions, shouldScroll, selectedStateSetter, multiSelect, onClose, onSearch) {
    const handleClose = useCallback(() => {
        setOpen(false);
        setSearchValue("");
        onSearch && onSearch("");
        onClose && onClose();
        if (selectedOptions.length > 0) {
            shouldScroll.current = true;
        }
    }, [setOpen, setSearchValue, onClose, onSearch, selectedOptions.length]);
    const handleSelection = useCallback((selection) => {
        if (multiSelect) {
            handleMultiSelect(selectedStateSetter, selectedOptions, selection);
        }
        else {
            handleSingleSelect(selectedStateSetter, selection);
        }
    }, [multiSelect, selectedStateSetter, selectedOptions]);
    const handleSingleSelect = useCallback((selectCallback, selection) => {
        selectCallback([selection]);
        handleClose();
    }, []);
    return {
        handleClose,
        handleSelection,
    };
}
function handleMultiSelect(selectCallback, selected, selection) {
    if (selected.some(s => s.id === selection.id)) {
        selectCallback(selected.filter(s => s.id !== selection.id));
    }
    else {
        selectCallback([...selected, selection]);
    }
}

function useCombobox(selected, onSelect, options, onClose, multiSelect, onSearch, debounceTime = 300) {
    const wrapperRef = useRef(null);
    const shouldScroll = useRef(false);
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const searchCallback = useCallback(debounce((value) => onSearch === null || onSearch === void 0 ? void 0 : onSearch(value), debounceTime), [onSearch, debounceTime]);
    const { handleClose, handleSelection } = useMakeComboboxHandlers(setOpen, setSearchValue, selected, shouldScroll, onSelect, multiSelect, onClose, onSearch);
    const internalFilteredOptions = options.filter(option => option.label.toLowerCase().includes(searchValue.toLowerCase()));
    return {
        wrapperRef,
        searchValue,
        setSearchValue,
        open,
        setOpen,
        selectedOptions: selected,
        selectedStateSetter: onSelect,
        shouldScroll,
        handleClose,
        handleSelection,
        internalFilteredOptions,
        handleSearchChange: onSearch ? searchCallback : noop,
    };
}

const COMBOBOX_TRIGGER_COUNT_ERROR_MESSAGE = "Combobox must have exactly one Trigger element";
function useComboboxValidation(children) {
    const optionElements = getCompoundComponents(ComboboxOption, children);
    const actionElements = getCompoundComponents(ComboboxAction, children);
    const activatorElements = getCompoundComponents(ComboboxActivator, children);
    const shouldThrowTriggerError = isInvalid(activatorElements);
    useAssert_2(shouldThrowTriggerError, COMBOBOX_TRIGGER_COUNT_ERROR_MESSAGE);
    return {
        optionElements,
        triggerElement: activatorElements[0],
        actionElements,
    };
}
function isInvalid(activators) {
    return activators.length > 1 ? true : false;
}
/**
 * Return all instances child component that matches the `type` provided
 */
function getCompoundComponents(type, children) {
    const childrenArray = Children.toArray(children);
    const elements = childrenArray.filter((child) => isValidElement(child) && child.type === type);
    return elements;
}

function Combobox(props) {
    const { optionElements, triggerElement, actionElements } = useComboboxValidation(props.children);
    const options = useMemo(() => (optionElements === null || optionElements === void 0 ? void 0 : optionElements.map(option => ({
        id: option.props.id,
        label: option.props.label,
        prefix: option.props.prefix,
    }))) || [], [optionElements]);
    const { selectedOptions, selectedStateSetter, shouldScroll, wrapperRef, searchValue, setSearchValue, open, setOpen, handleClose, handleSelection, internalFilteredOptions, handleSearchChange, } = useCombobox(props.selected, props.onSelect, options, props.onClose, props.multiSelect, props.onSearch, props.onSearchDebounce);
    return (React.createElement(ComboboxContextProvider, { selected: selectedOptions, selectionHandler: handleSelection, open: open, setOpen: setOpen, handleClose: handleClose, shouldScroll: shouldScroll, searchValue: searchValue },
        React.createElement("div", { ref: wrapperRef, className: styles.wrapper },
            open && (React.createElement("div", { className: styles.overlay, onClick: () => handleClose(), "data-testid": "ATL-Combobox-Overlay" })),
            triggerElement || (React.createElement(ComboboxTrigger, { label: props.label, selected: props.selected })),
            React.createElement(ComboboxContent, { multiselect: props.multiSelect, subjectNoun: props.subjectNoun, selected: selectedOptions, actionElements: actionElements, selectedStateSetter: selectedStateSetter, handleSelection: handleSelection, searchValue: searchValue, setSearchValue: setSearchValue, wrapperRef: wrapperRef, open: open, setOpen: setOpen, options: props.onSearch ? options : internalFilteredOptions, loading: props.loading, handleSearchChange: handleSearchChange, onLoadMore: props.onLoadMore }))));
}
Combobox.Activator = ComboboxActivator;
Combobox.Action = ComboboxAction;
Combobox.Option = ComboboxOption;

export { Combobox as C };
